import { type ReactNode, useCallback, useState } from 'react';

import { Col } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { useAtomsToastManager } from '@opendoor/atoms/toast';
import type { HubBuyingTour } from 'components/hub/useHubBuyerListingsAndTours';
import type { HubBuyingTourFeedback } from 'components/hub/useHubTourFeedbacks';

import { HubFilterDropdown } from './HubFilterDropdown';
import { HubSectionHeader } from './HubSectionHeader';
import { HomeToursSkeleton } from './HubSkeletons';
import { TourCancelDialog } from './TourCancelDialog';
import { TourCard } from './TourCard';
import {
  getTourFeedbackConfirmationMessage,
  type QuickTourFeedbackSentiment,
} from './tourFeedback';
import { useHomeTours } from './useHomeTours';
import type { HubTour } from './useHomeTours';
import { useTourCancel } from './useTourCancel';
import { useTourFeedback } from './useTourFeedback';

type TourFilter = 'upcoming' | 'past' | 'all';

// "All" is only meaningful when both buckets have tours. Otherwise the section
// offers a simple Upcoming/Completed toggle, with an empty state for whichever
// bucket is currently empty.
const TOUR_TOGGLE_OPTIONS: ReadonlyArray<{ value: TourFilter; label: string }> = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Completed' },
];
const TOUR_FILTER_OPTIONS: ReadonlyArray<{ value: TourFilter; label: string }> = [
  ...TOUR_TOGGLE_OPTIONS,
  { value: 'all', label: 'All' },
];

const TOUR_EMPTY_STATE_COPY: Record<'upcoming' | 'past', { title: string; body: string }> = {
  upcoming: {
    title: 'No upcoming tours',
    body: 'When you schedule a home tour, it’ll show up here.',
  },
  past: {
    title: 'No completed tours',
    body: 'Your completed tours will show up here.',
  },
};

const TourEmptyState = ({ bucket }: { bucket: 'upcoming' | 'past' }) => {
  const copy = TOUR_EMPTY_STATE_COPY[bucket];
  return (
    <Col className="items-center justify-center gap-1 rounded-xl bg-od-bg-secondary px-5 py-10 text-center">
      <Text as="p" className="text-label-md text-od-content-primary">
        {copy.title}
      </Text>
      <Text as="p" className="text-body-sm text-od-content-secondary">
        {copy.body}
      </Text>
    </Col>
  );
};

interface HomeToursSectionProps {
  tours: HubBuyingTour[];
  tourFeedbacks: HubBuyingTourFeedback[];
  isBuyerDataLoading: boolean;
  isTourFeedbackLoading: boolean;
}

const FEEDBACK_TOAST_DURATION_MS = 3000;

type CancelDialogState =
  | { kind: 'closed' }
  | { kind: 'confirming'; tour: HubTour; hasError: boolean }
  | { kind: 'canceling'; tour: HubTour };

export function HomeToursSection({
  tours,
  tourFeedbacks,
  isBuyerDataLoading,
  isTourFeedbackLoading,
}: HomeToursSectionProps) {
  const { upcoming, past, isLoading } = useHomeTours(tours, isBuyerDataLoading);
  const { feedbackByAppointmentId, submit: submitTourFeedback } = useTourFeedback(tourFeedbacks);
  const { canceledTourIds, cancel: cancelTour } = useTourCancel();
  const [selectedFilter, setSelectedFilter] = useState<TourFilter | null>(null);
  const [cancelDialog, setCancelDialog] = useState<CancelDialogState>({ kind: 'closed' });
  const toasts = useAtomsToastManager();

  const hasUpcoming = upcoming.length > 0;
  // A tour canceled this visit can end before the next load drops it; it must not surface as
  // a completed tour with feedback chips.
  const visiblePast = past.filter((tour) => !canceledTourIds.has(tour.id));
  const hasPast = visiblePast.length > 0;

  const handleChipFeedback = useCallback(
    async function handleChipFeedback(tour: HubTour, sentiment: QuickTourFeedbackSentiment) {
      const didSubmit = await submitTourFeedback(tour, sentiment);
      if (!didSubmit) {
        return;
      }

      toasts.add({
        variant: 'success',
        title: getTourFeedbackConfirmationMessage(sentiment),
        timeout: FEEDBACK_TOAST_DURATION_MS,
      });
    },
    [submitTourFeedback, toasts],
  );

  const openCancelDialog = useCallback(function openCancelDialog(tour: HubTour) {
    setCancelDialog({ kind: 'confirming', tour, hasError: false });
  }, []);

  const closeCancelDialog = useCallback(function closeCancelDialog() {
    setCancelDialog({ kind: 'closed' });
  }, []);

  // Read the tour from the current list rather than the snapshot taken when the dialog opened:
  // the boundary timer turns canCancel off at the start time, and the server does not re-check.
  const cancellableDialogTour =
    cancelDialog.kind === 'confirming'
      ? upcoming.find((tour) => tour.id === cancelDialog.tour.id && tour.canCancel)
      : undefined;
  const isCancelDialogOpen =
    cancelDialog.kind === 'canceling' || cancellableDialogTour !== undefined;

  const confirmCancel = useCallback(
    async function confirmCancel() {
      if (cancellableDialogTour === undefined) {
        setCancelDialog({ kind: 'closed' });
        return;
      }

      const tour = cancellableDialogTour;
      setCancelDialog({ kind: 'canceling', tour });
      const didCancel = await cancelTour(tour);
      setCancelDialog(
        didCancel ? { kind: 'closed' } : { kind: 'confirming', tour, hasError: true },
      );
    },
    [cancellableDialogTour, cancelTour],
  );

  const handleConfirmCancel = useCallback(
    function handleConfirmCancel() {
      void confirmCancel();
    },
    [confirmCancel],
  );

  const renderUpcomingTour = useCallback(
    function renderUpcomingTour(tour: HubTour) {
      return (
        <TourCard
          key={tour.id}
          section="home-tours"
          tour={tour}
          isCanceled={canceledTourIds.has(tour.id)}
          onCancel={openCancelDialog}
        />
      );
    },
    [canceledTourIds, openCancelDialog],
  );

  const renderPastTour = useCallback(
    function renderPastTour(tour: HubTour) {
      return (
        <TourCard
          key={tour.id}
          section="home-tours"
          tour={tour}
          feedback={feedbackByAppointmentId[tour.id]}
          onFeedback={isTourFeedbackLoading ? undefined : handleChipFeedback}
        />
      );
    },
    [feedbackByAppointmentId, handleChipFeedback, isTourFeedbackLoading],
  );

  // A buyer with no tours does not see the section. Gate on the hook's derived
  // buckets (not the raw `tours` prop) so visibility matches what actually
  // renders and we don't re-parse the tour list.
  if (!isLoading && !hasUpcoming && !hasPast) {
    return null;
  }

  // Keep the dropdown whenever there are completed tours, so a buyer with no
  // upcoming tours can still toggle to "Upcoming" and see its empty state.
  const showFilter = hasPast;
  const filterOptions = hasUpcoming && hasPast ? TOUR_FILTER_OPTIONS : TOUR_TOGGLE_OPTIONS;
  // Always lead with "Upcoming" (showing its empty state when there are none) so
  // the section surfaces what's next first; honor an explicit selection only
  // while the dropdown is visible and the option is still offered.
  const defaultFilter: TourFilter = 'upcoming';
  const filter =
    showFilter &&
    selectedFilter != null &&
    filterOptions.some((option) => option.value === selectedFilter)
      ? selectedFilter
      : defaultFilter;

  const showUpcoming = filter !== 'past';
  const showPast = filter !== 'upcoming';

  let emptyBucket: 'upcoming' | 'past' | null = null;
  if (filter === 'upcoming' && !hasUpcoming) {
    emptyBucket = 'upcoming';
  } else if (filter === 'past' && !hasPast) {
    emptyBucket = 'past';
  }

  let toursBody: ReactNode;
  if (isLoading) {
    toursBody = <HomeToursSkeleton />;
  } else if (emptyBucket) {
    toursBody = <TourEmptyState bucket={emptyBucket} />;
  } else {
    toursBody = (
      <>
        {showUpcoming && upcoming.map(renderUpcomingTour)}
        {showPast && visiblePast.map(renderPastTour)}
      </>
    );
  }

  return (
    <Col className="w-full gap-4">
      <HubSectionHeader
        title="Home tours"
        action={
          showFilter ? (
            <HubFilterDropdown
              value={filter}
              options={filterOptions}
              onChange={setSelectedFilter}
              ariaLabel="Filter home tours"
            />
          ) : undefined
        }
      />
      {toursBody}
      <TourCancelDialog
        open={isCancelDialogOpen}
        canceling={cancelDialog.kind === 'canceling'}
        hasError={cancelDialog.kind === 'confirming' && cancelDialog.hasError}
        onKeep={closeCancelDialog}
        onConfirm={handleConfirmCancel}
      />
    </Col>
  );
}
